import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { agentApi, apiErrorMessage } from '../services/agentApi';

/**
 * Approve-by-email-link landing page (AGENT_MAILBOX_PLAN.md Phase 5). The
 * link only shows the action; the decision is an explicit click (a POST) so
 * email security scanners that pre-fetch links can never approve anything.
 * Token = single use, 7-day expiry, stored hashed server-side.
 */
const AgentApprove: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const initialDecision = params.get('decision') === 'reject' ? 'reject' : 'approve';
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof agentApi.approvalPreview>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!token) {
      setError('This link is missing its token.');
      return;
    }
    agentApi.approvalPreview(token).then(setPreview).catch((err) => setError(apiErrorMessage(err, 'This approval link is invalid.')));
  }, [token]);

  const act = async (decision: 'approve' | 'reject') => {
    setBusy(true);
    setError(null);
    try {
      await agentApi.approvalAct(token, decision, note || undefined);
      setDone(decision === 'approve' ? 'Approved — Arie is continuing the run.' : 'Rejected — Arie will continue without it.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not record your decision'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow p-6">
        <div className="text-primary font-semibold">Arie · RiskNinja</div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mt-2">Approval request</h1>
        {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {!preview && !error && <p className="mt-4 text-sm text-gray-500">Loading…</p>}
        {preview && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3">
              <div className="text-sm font-medium text-gray-900 dark:text-white">{preview.action.displayName}</div>
              {preview.reason && <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">{preview.reason}</div>}
            </div>
            {done ? (
              <p className="text-sm text-green-700 dark:text-green-400">{done}</p>
            ) : preview.status !== 'pending' ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">This request was already {preview.status}.</p>
            ) : (
              <>
                <input className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" placeholder="Note for Arie (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                <div className="flex gap-2">
                  <button onClick={() => act('approve')} disabled={busy} className={`flex-1 px-3 py-2 rounded-md text-white text-sm font-medium ${initialDecision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500/80 hover:bg-green-600'} disabled:opacity-50`}>Approve</button>
                  <button onClick={() => act('reject')} disabled={busy} className="flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50">Reject</button>
                </div>
              </>
            )}
            <Link to={`/agent?run=${preview.runId}`} className="block text-sm text-primary hover:underline">Open the run in RiskNinja →</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentApprove;
